/**
 * Cognito OAuth2 PKCE authentication.
 *
 * Cognito domain, client ID, identity provider, and OAuth scopes are sourced
 * from environment variables (injected at build time by react-scripts) so
 * Beta, Gamma, and Prod each target their own isolated pools. No hardcoded
 * fallback values live in this file (pipe-003, rev-042, simp-011): a missing
 * env var fails loud at module load rather than silently redirecting a
 * developer build at a shared production pool. Dev defaults live in
 * `.env.development` which ships non-sensitive dev-pool values.
 */

import { errorMessage } from './errors';

function requireEnv(name: string): string {
  const value = process.env[name];
  if (!value) {
    throw new Error(
      `${name} is not set. Configure it in .env.development (local) or the build pipeline (Beta/Gamma/Prod).`,
    );
  }
  return value;
}

const COGNITO_DOMAIN = requireEnv('REACT_APP_COGNITO_DOMAIN');
const CLIENT_ID = requireEnv('REACT_APP_COGNITO_CLIENT_ID');
const IDENTITY_PROVIDER = requireEnv('REACT_APP_COGNITO_IDP');
const SCOPES = process.env.REACT_APP_COGNITO_SCOPES || 'openid email phone';
const REDIRECT_URI = window.location.origin;

/** Authenticated user session data. */
export interface AuthUser {
  email: string;
  name: string;
  idToken: string;
}

/**
 * Reason code for a failed OAuth callback exchange. Surfaced to the UI so
 * distinct failures ('session lost' vs 'token endpoint down' vs 'malformed
 * token response') can produce meaningful user messages instead of a single
 * opaque fallback (simp-008).
 */
export type CallbackErrorReason =
  | 'no_verifier'
  | 'token_http_error'
  | 'missing_id_token'
  | 'network_error'
  | 'parse_error';

/**
 * Discriminated return type for handleCallback().
 *   - 'no_code'  : the page was loaded without a ?code= param (not a callback).
 *   - 'ok'       : token exchange succeeded; `user` is populated.
 *   - 'error'    : token exchange failed; `reason` narrows the cause and
 *                  `message` carries a user-facing string.
 */
export type CallbackResult =
  | { status: 'no_code' }
  | { status: 'ok'; user: AuthUser }
  | { status: 'error'; reason: CallbackErrorReason; message: string };

/**
 * Convert a byte array to Base64URL (RFC 4648 §5) — used for PKCE verifier
 * and challenge. Uses Array.from + .join('') instead of spread syntax so
 * large buffers cannot hit the call-stack argument limit.
 *
 * Not exported (simp-009): implementation detail of the PKCE flow.
 */
function toBase64URL(bytes: Uint8Array): string {
  const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join('');
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Generate a random string for PKCE code verifier. */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return toBase64URL(array);
}

/** Compute SHA-256 hash for PKCE code challenge. */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  try {
    const digest = await crypto.subtle.digest('SHA-256', data);
    return toBase64URL(new Uint8Array(digest));
  } catch (err: unknown) {
    throw new Error(`Failed to compute PKCE challenge: ${errorMessage(err)}`);
  }
}

/**
 * Redirect to Cognito hosted UI for SSO login.
 *
 * rev-014: the PKCE challenge await is wrapped in try/catch so callers get a
 * typed Error they can surface in the UI instead of an unhandled rejection.
 */
export async function login(): Promise<void> {
  try {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem('pkce_verifier', verifier);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: SCOPES,
      code_challenge_method: 'S256',
      code_challenge: challenge,
      identity_provider: IDENTITY_PROVIDER,
    });

    window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
  } catch (err: unknown) {
    throw new Error(`Failed to start login: ${errorMessage(err)}`);
  }
}

/**
 * Exchange the OAuth code + PKCE verifier for an id_token.
 * Returns a CallbackResult on error or a raw id_token string on success so
 * handleCallback stays a thin orchestrator over this + parseIdToken.
 */
async function exchangeCodeForIdToken(
  code: string,
  verifier: string,
): Promise<
  | { ok: true; idToken: string }
  | { ok: false; result: CallbackResult }
> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });

  let response: Response;
  try {
    response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (err: unknown) {
    return {
      ok: false,
      result: {
        status: 'error',
        reason: 'network_error',
        message: `Could not reach the sign-in service: ${errorMessage(err)}`,
      },
    };
  }

  if (!response.ok) {
    return {
      ok: false,
      result: {
        status: 'error',
        reason: 'token_http_error',
        message: `Sign-in service rejected the request (HTTP ${response.status}).`,
      },
    };
  }

  try {
    const tokens = (await response.json()) as Record<string, string>;
    if (!tokens.id_token) {
      return {
        ok: false,
        result: {
          status: 'error',
          reason: 'missing_id_token',
          message: 'Sign-in response did not include an ID token.',
        },
      };
    }
    return { ok: true, idToken: tokens.id_token };
  } catch (err: unknown) {
    return {
      ok: false,
      result: {
        status: 'error',
        reason: 'parse_error',
        message: `Could not parse the sign-in response: ${errorMessage(err)}`,
      },
    };
  }
}

/** Decode a JWT id_token into an AuthUser. Throws on malformed tokens. */
function parseIdToken(idToken: string): AuthUser {
  const payload = JSON.parse(atob(idToken.split('.')[1])) as Record<string, string>;
  return {
    email: payload.email || '',
    name: payload.name || payload.email || 'User',
    idToken,
  };
}

/**
 * Exchange the OAuth authorization code for tokens and return a discriminated
 * result so callers can distinguish 'not a callback' from 'exchange failed'
 * (simp-008). Every branch that previously returned `null` now carries a
 * reason code and a user-facing message.
 */
export async function handleCallback(): Promise<CallbackResult> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return { status: 'no_code' };

  const verifier = sessionStorage.getItem('pkce_verifier');
  if (!verifier) {
    return {
      status: 'error',
      reason: 'no_verifier',
      message: 'Your sign-in session expired before we could finish logging you in. Please sign in again.',
    };
  }

  window.history.replaceState({}, '', window.location.pathname);
  sessionStorage.removeItem('pkce_verifier');

  let exchange: Awaited<ReturnType<typeof exchangeCodeForIdToken>>;
  try {
    exchange = await exchangeCodeForIdToken(code, verifier);
  } catch (err: unknown) {
    // exchangeCodeForIdToken catches all its own failures, but keep a belt-
    // and-suspenders guard here so rev-014 (no bare await) stays satisfied
    // and any future refactor that lets an exception through is captured.
    return {
      status: 'error',
      reason: 'network_error',
      message: `Sign-in failed unexpectedly: ${errorMessage(err)}`,
    };
  }
  if (!exchange.ok) return exchange.result;

  try {
    const user = parseIdToken(exchange.idToken);
    sessionStorage.setItem('auth_user', JSON.stringify(user));
    return { status: 'ok', user };
  } catch (err: unknown) {
    return {
      status: 'error',
      reason: 'parse_error',
      message: `Could not parse the sign-in response: ${errorMessage(err)}`,
    };
  }
}

/** Retrieve the current authenticated user from session storage. */
export function getUser(): AuthUser | null {
  const stored = sessionStorage.getItem('auth_user');
  if (!stored) return null;
  try {
    return JSON.parse(stored) as AuthUser;
  } catch {
    return null;
  }
}

/** Clear session and redirect to Cognito logout endpoint. */
export function logout(): void {
  sessionStorage.removeItem('auth_user');
  sessionStorage.removeItem('pkce_verifier');
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: REDIRECT_URI,
  });
  window.location.href = `${COGNITO_DOMAIN}/logout?${params}`;
}
